import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'Python / C++ / Go',
    'PyTorch / MONAI / nnU-Net',
    'Hugging Face / PEFT / LoRA',
    'AWS / Kubernetes / Docker',
    'Terraform / ArgoCD / GitLab',
    'OpenCV / Open3D / 3D Slicer',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              中国出身、日本在住のAI・クラウドエンジニアです。
              <a href="https://www.nagoya-u.ac.jp/" target="_blank" rel="noreferrer">
                名古屋大学大学院
              </a>
              にて医用画像解析（CT/MRI/内視鏡）の研究で
              <a href="https://www.nagoya-u.ac.jp/" target="_blank" rel="noreferrer">
                博士号
              </a>
              を取得後、
              同大学の研究員として医療AIの研究開発に従事してまいりました。
            </p>

            <p>
              研究では、少数アノテーションを用いたCT/MRI画像の臓器・病変セグメンテーション、
              内視鏡手術映像からの3D再構成、CT画像と自然言語を組み合わせた
              自動診断VLMの開発に取り組みました。研究成果は国際学術誌3件・国際会議4件に採択され、
              <a href="https://in-body-ca.org/about.html" target="_blank" rel="noreferrer">
                2025年大阪・関西万博
              </a>
              にも展示されました。
            </p>

            <p>
              現在は
              <a href="https://www.astemo.com/" target="_blank" rel="noreferrer">
                株式会社Astemo
              </a>
              にて、自動運転アルゴリズムのクラウドテスト基盤の開発・運用を担当しています。
              日本語・英語・中国語でのコミュニケーションが可能で、
              海外チームとの技術調整経験もあります。
            </p>

            <p>最近よく使っている技術スタックです：</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Luyang Zhang"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
